import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Play, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { ScrollReveal, SectionLabel, EASE } from "./Reveal";
import { LINKS } from "@/constants/links";

const SHOWREEL_IMG =
  "https://images.unsplash.com/photo-1576280314550-773c50583407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxmaWxtbWFrZXIlMjBjYW1lcmElMjBsZW5zJTIwY3JlYXRpdmUlMjBhZ2VuY3klMjBzdHVkaW8lMjBkYXJrbmVzcyUyMHNoYWRvd3xlbnwwfHx8fDE3ODk1MDkyMDl8MA&ixlib=rb-4.1.0&q=85";

const getEmbed = (url) => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]+)/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1` };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
  return { type: "video", src: url };
};

export const ShowreelSection = () => {
  const [playing, setPlaying] = useState(false);
  const previewRef = useRef(null);
  const previewInView = useInView(previewRef, { amount: 0.4 });
  const embed = LINKS.showreel ? getEmbed(LINKS.showreel) : null;

  useEffect(() => {
    const v = previewRef.current;
    if (!v) return;
    if (previewInView && !playing) v.play().catch(() => {});
    else v.pause();
  }, [previewInView, playing]);

  const onPlay = () => {
    if (embed) setPlaying(true);
    else toast.info("Showreel coming soon — drop the video link into links.js to embed it.");
  };

  return (
    <section data-testid="showreel-section" className="py-24 lg:py-32 bg-[#111111] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <SectionLabel index="08" title="Showreel" />
          <h2 className="font-display leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">Press play.</h2>
          <p className="mt-6 text-base md:text-lg text-[#8A8A85] max-w-xl">
            A collection of moments, stories and brands I've had the privilege to work with.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div
            data-testid="showreel-player"
            onClick={onPlay}
            className="group relative mt-12 aspect-video overflow-hidden border border-white/10 cursor-pointer"
          >
            {LINKS.showreel ? (
              <video
                ref={previewRef}
                src={LINKS.showreel}
                poster={SHOWREEL_IMG}
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-700"
              />
            ) : (
              <img
                src={SHOWREEL_IMG}
                alt="Etia Media showreel"
                className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-[opacity,transform] duration-700"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-[#F2F0EB] text-[#0A0A0A] flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <Play className="w-7 h-7 fill-current ml-1" />
              </span>
            </div>
            <span
              data-testid="showreel-sound-badge"
              className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/20 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F2F0EB]"
            >
              <Play className="w-3 h-3 fill-current" />
              Tap to play with sound
            </span>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
            <p className="text-base text-[#8A8A85]">Want something like this for your business?</p>
            <button
              data-testid="showreel-start-project-btn"
              onClick={() => window.__openEnquiry()}
              className="group flex items-center gap-3 font-display text-sm tracking-[0.08em] border-b border-[#F2F0EB] pb-2 w-fit hover:gap-5 transition-all duration-300"
            >
              Start a Project
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </ScrollReveal>
      </div>

      <AnimatePresence>
        {playing && embed && (
          <motion.div
            data-testid="showreel-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[90] bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setPlaying(false)}
          >
            <button
              data-testid="showreel-close-btn"
              className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-full max-w-5xl aspect-video border border-white/10" onClick={(e) => e.stopPropagation()}>
              {embed.type === "iframe" ? (
                <iframe
                  src={embed.src}
                  title="Etia Media showreel"
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={embed.src} className="w-full h-full" controls autoPlay playsInline />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
